(function () {
  'use strict';

  const DESKTOP_MIN = 1025;
  const TRANSITION_MS = 400;
  const SWIPE_THRESHOLD = 10;

  const cardState = new WeakMap();
  let lastIsDesktop = window.innerWidth >= DESKTOP_MIN;
  let resizeTimer;

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  function getScopedRoot(container) {
    if (!container || container === document) {
      return document.getElementById('ProductGridContainer') || document;
    }
    if (container.id === 'ProductGridContainer' || container.classList?.contains('product-grid-container')) {
      return container;
    }
    return document.getElementById('ProductGridContainer') || container;
  }

  function getCards(container) {
    const root = getScopedRoot(container);
    if (root.id === 'ProductGridContainer') {
      return root.querySelectorAll('#product-grid .product-card-wrapper.plp-multi-image');
    }
    return root.querySelectorAll('.product-grid-container #product-grid .product-card-wrapper.plp-multi-image');
  }

  function resetInlineImageStyles(card) {
    const firstImage = card.querySelector('.product-first-image');
    const swiperWrapper = card.querySelector('.product-swiper-wrapper');
    if (firstImage) firstImage.style.removeProperty('display');
    if (swiperWrapper) swiperWrapper.style.removeProperty('display');
  }

  function showFirstImage(card) {
    if (isDesktop()) {
      card.classList.remove('plp-swiper-active');
      return;
    }
    const firstImage = card.querySelector('.product-first-image');
    const swiperWrapper = card.querySelector('.product-swiper-wrapper');
    if (firstImage) firstImage.style.display = 'block';
    if (swiperWrapper) swiperWrapper.style.display = 'none';
  }

  function showSwiper(card) {
    if (isDesktop()) {
      card.classList.add('plp-swiper-active');
      return;
    }
    const firstImage = card.querySelector('.product-first-image');
    const swiperWrapper = card.querySelector('.product-swiper-wrapper');
    if (firstImage) firstImage.style.display = 'none';
    if (swiperWrapper) swiperWrapper.style.display = 'block';
  }

  function getImageCount(card) {
    const swiperEl = card.querySelector('.product-card-swiper');
    if (!swiperEl) return 0;
    const count = parseInt(swiperEl.dataset.imageCount, 10);
    if (!Number.isNaN(count)) return count;
    return card.querySelectorAll('.product-card-swiper .swiper-slide').length;
  }

  function shouldInitSwiper(card) {
    return getImageCount(card) > 1;
  }

  function updateZoom(swiperEl, activeIndex) {
    if (!swiperEl) return;
    if (activeIndex === 1) {
      swiperEl.classList.add('is-zoom-active');
    } else {
      swiperEl.classList.remove('is-zoom-active');
    }
  }

  function addSwipeGuard(card, swiper) {
    const link = card.querySelector('a.full-unstyled-link');
    if (!link || link.dataset.plpSwipeGuard === 'true') return;
    link.dataset.plpSwipeGuard = 'true';

    let touchStartX = 0;
    let touchStartY = 0;
    let moved = false;

    link.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        moved = false;
      },
      { passive: true }
    );

    link.addEventListener(
      'touchmove',
      (event) => {
        const dx = Math.abs(event.touches[0].clientX - touchStartX);
        const dy = Math.abs(event.touches[0].clientY - touchStartY);
        if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
          moved = true;
        }
      },
      { passive: true }
    );

    link.addEventListener('click', (event) => {
      if (moved || (swiper && swiper.allowClick === false)) {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }

  function createSwiper(card, options) {
    const swiperEl = card.querySelector('.product-card-swiper');
    if (!swiperEl || typeof Swiper === 'undefined') return null;

    const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');

    const swiper = new Swiper(swiperEl, {
      loop: false,
      slidesPerView: 1,
      spaceBetween: 0,
      preventClicks: true,
      preventClicksPropagation: true,
      threshold: SWIPE_THRESHOLD,
      scrollbar: scrollbarEl
        ? {
            el: scrollbarEl,
            draggable: true,
            hide: false,
          }
        : undefined,
      on: {
        init(swiperInstance) {
          updateZoom(swiperEl, swiperInstance.activeIndex);
        },
        slideChange(swiperInstance) {
          updateZoom(swiperEl, swiperInstance.activeIndex);
        },
      },
    });

    addSwipeGuard(card, swiper);

    if (options && options.slideToIndex !== undefined) {
      swiper.slideTo(options.slideToIndex, 0);
      updateZoom(swiperEl, options.slideToIndex);
    }

    return swiper;
  }

  function destroySwiper(card) {
    const state = cardState.get(card);
    if (!state) return;

    if (state.destroyTimer) {
      clearTimeout(state.destroyTimer);
      state.destroyTimer = null;
    }

    if (state.swiper) {
      state.swiper.destroy(true, true);
      state.swiper = null;
    }

    const swiperEl = card.querySelector('.product-card-swiper');
    if (swiperEl) swiperEl.classList.remove('is-zoom-active');

    card.classList.remove('plp-swiper-active');
    card.removeAttribute('data-plp-swiper-init');

    if (isDesktop()) {
      showFirstImage(card);
    } else {
      showSwiper(card);
    }

    cardState.delete(card);
  }

  function initMobileCard(card) {
    if (!shouldInitSwiper(card) || card.hasAttribute('data-plp-swiper-init')) return;

    resetInlineImageStyles(card);
    showSwiper(card);

    const swiper = createSwiper(card);
    if (!swiper) return;

    card.setAttribute('data-plp-swiper-init', 'true');
    cardState.set(card, { swiper, mode: 'mobile', destroyTimer: null });
  }

  function bindDesktopHover(card) {
    if (card.dataset.plpDesktopBound === 'true') return;
    card.dataset.plpDesktopBound = 'true';

    card.addEventListener('mouseenter', () => {
      if (!isDesktop() || !shouldInitSwiper(card)) return;

      let state = cardState.get(card) || { destroyTimer: null };

      if (state.destroyTimer) {
        clearTimeout(state.destroyTimer);
        state.destroyTimer = null;
      }

      showSwiper(card);

      if (!state.swiper) {
        const swiper = createSwiper(card, { slideToIndex: 1 });
        if (!swiper) return;
        state.swiper = swiper;
        state.mode = 'desktop';
        card.setAttribute('data-plp-swiper-init', 'true');
      } else {
        state.swiper.slideTo(1, 0);
        updateZoom(card.querySelector('.product-card-swiper'), 1);
      }

      cardState.set(card, state);
    });

    card.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;

      const state = cardState.get(card);
      const swiperEl = card.querySelector('.product-card-swiper');

      if (swiperEl) swiperEl.classList.remove('is-zoom-active');
      showFirstImage(card);

      if (!state) return;

      state.destroyTimer = setTimeout(() => {
        destroySwiper(card);
      }, TRANSITION_MS);
    });
  }

  function initDesktopCard(card) {
    if (!shouldInitSwiper(card)) return;

    resetInlineImageStyles(card);
    showFirstImage(card);
    bindDesktopHover(card);
  }

  function destroyAllCards(container) {
    getCards(container).forEach((card) => {
      destroySwiper(card);
      resetInlineImageStyles(card);
    });
  }

  function initializeProductCardSwipers(container) {
    if (typeof Swiper === 'undefined') return;

    const root = getScopedRoot(container);
    const currentIsDesktop = isDesktop();

    if (currentIsDesktop !== lastIsDesktop) {
      lastIsDesktop = currentIsDesktop;
      destroyAllCards(root);
    }

    getCards(root).forEach((card) => {
      if (!shouldInitSwiper(card)) return;

      if (currentIsDesktop) {
        initDesktopCard(card);
      } else if (!card.hasAttribute('data-plp-swiper-init')) {
        initMobileCard(card);
      }
    });
  }

  window.initializeProductCardSwipers = initializeProductCardSwipers;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const plpGrid = document.getElementById('ProductGridContainer');
      if (!plpGrid) return;

      const currentIsDesktop = isDesktop();
      if (currentIsDesktop === lastIsDesktop) return;

      lastIsDesktop = currentIsDesktop;
      destroyAllCards(plpGrid);
      initializeProductCardSwipers(plpGrid);
    }, 150);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const plpGrid = document.getElementById('ProductGridContainer');
    if (plpGrid) {
      initializeProductCardSwipers(plpGrid);
    }
  });
})();
